import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import classNames from 'classnames';
import { useFilters } from 'hooks/useFilters';
import throttle from 'lodash.throttle';

import { show } from 'store/slices/alert';
import {
  certificateSelector,
  download,
  filter,
} from 'store/slices/certificate';
import { viewModeSelector } from 'store/slices/viewMode';

import { VIEW_MODE } from 'utils/viewMode';

import Loader from 'components/Loader/Loader';
import Button from 'components/UI/Button/Button';
import RippleElement from 'components/UI/RippleElement/RippleElement';
import SearchInput from 'components/UI/SearchInput/SearchInput';
import Select from 'components/UI/Select/Select';
import ToggleFilters from 'components/UI/ToggleFilters/ToggleFilters';
import Tooltip from 'components/UI/Tooltip/Tooltip';
import Typography from 'components/UI/Typography/Typography';

import { GAME_TYPE_OPTIONS } from 'components/config/certificate';
import {
  Country,
  ITALY_DOWNLOAD_MSG,
  getCountryMap,
} from 'components/config/certificate';
import {
  gameJurisdictionListPath,
  gamePath,
  getCertificate,
} from 'components/config/cloud';
import { fillDate } from 'components/config/filters';

import { ReactComponent as Download2Icon } from 'images/Icons/Download2.svg';
import { ReactComponent as DownloadIcon } from 'images/Icons/Download.svg';

import styles from './Compliance.module.scss';

const Compliance = () => {
  const dispatch = useDispatch();

  const { filters, handleChange } = useFilters({
    type: GAME_TYPE_OPTIONS[0].value,
    releaseDates: [],
  });

  const inputRef = useRef();

  const data = useSelector(certificateSelector.getData);
  const loader = useSelector(certificateSelector.getLoading);
  const errors = useSelector(certificateSelector.getError);
  const filterStr = useSelector(certificateSelector.getFilter);
  const viewMode = useSelector(viewModeSelector.getViewMode);

  const filtersEnabled = useMemo(
    () => !!filters.releaseDates.length,
    [filters.releaseDates],
  );

  const columnsStyle = useMemo(
    () => ({
      gridTemplateColumns: `${viewMode === VIEW_MODE.SIMPLE ? 280 : 460}px repeat(${Country.length}, 1fr)`,
    }),
    [viewMode],
  );

  const handleFilter = useCallback(
    throttle((event) => {
      event.stopPropagation();
      event.preventDefault();

      dispatch(filter(event.target.value));
    }, 500),
    [],
  );

  const getCertificatePathByCountry = (game, country) =>
    `${gamePath(game.GameCode)}Certificates/${country}.zip`;

  const handleDownload = useCallback((game, country) => {
    if (country === 'All') {
      dispatch(
        download({
          zipFileName: `${game.Game} [Certificates]`,
          urls: [getCertificate(game.GameCode)],
        }),
      );
      return;
    }

    dispatch(
      download({
        zipFileName: `${game.Game} [Certificate - ${country}]`,
        urls: [getCertificatePathByCountry(game, country)],
      }),
    );
  }, []);

  const handleDownloadByCountry = useCallback((country) => {
    if (country === 'Italy') {
      dispatch(show({ type: 'info', text: ITALY_DOWNLOAD_MSG }));
      return;
    }

    const urls = data.reduce(
      (acc, item) =>
        item[country]
          ? acc.concat(getCertificatePathByCountry(item, country))
          : acc,
      [],
    );

    dispatch(
      download({
        zipFileName: `${country} [Certificates]`,
        schema: 'certificates',
        urls,
      }),
    );
  }, []);

  useEffect(() => {
    errors && dispatch(show({ type: 'error', text: errors }));
  }, [errors]);

  useEffect(() => {
    if (filterStr.length && inputRef) {
      inputRef.current.value = filterStr;
    }
  }, [inputRef]);

  return (
    <div className={styles.wrapper}>
      <Typography type="heading-md" weight="medium" uppercase>
        Game certificates
      </Typography>
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.filters}>
            <ToggleFilters
              name="type"
              value={filters.type}
              options={GAME_TYPE_OPTIONS}
              onChange={handleChange}
            />
            <div className={styles.searchWrapper}>
              <SearchInput
                ref={inputRef}
                handleChange={handleFilter}
                placeholder="Country, game id, marketing name, abs..."
                customStyle={styles.searchInput}
              />
              <Select
                title="Release Date"
                label="releaseDates"
                value={filters.releaseDates}
                items={fillDate(2017)}
                handleSelect={handleChange}
                width={200}
                widthDropdown={265}
                countTags={2}
                multiple
              />
              {filtersEnabled && (
                <Button
                  variant="text"
                  sx={{
                    textDecoration: 'underline',
                  }}
                >
                  Clear filters
                </Button>
              )}
            </div>
          </div>
          <Button
            variant="icon"
            target="_blank"
            rel="noreferrer"
            component={Link}
            startIcon={<DownloadIcon className={styles.downloadIcon} />}
            to={gameJurisdictionListPath}
            download
          >
            All certificates
          </Button>
        </div>
        <div className={styles.table}>
          <div className={styles.tHead} style={columnsStyle}>
            <div>Game name</div>
            {Country.map((country) => (
              <div key={country}>{country}</div>
            ))}
          </div>
          <div className={styles.tBody}>
            <div className={styles.tRow} style={columnsStyle}>
              <div />
              {Country.map((country) => (
                <RippleElement
                  key={country}
                  onClick={() =>
                    handleDownloadByCountry(getCountryMap(country))
                  }
                >
                  <span>All</span>
                  <Download2Icon />
                </RippleElement>
              ))}
            </div>
            {data.map((item) => (
              <div key={item.Game} className={styles.tRow} style={columnsStyle}>
                <RippleElement onClick={() => handleDownload(item, 'All')}>
                  {item.Game}
                  <Download2Icon />
                </RippleElement>
                {Country.map((country) => (
                  <RippleElement
                    key={`${item.Game}-${country}`}
                    className={classNames({
                      [styles.empty]: ![1, 2].includes(
                        item[getCountryMap(country)],
                      ),
                    })}
                    disabled={![1, 2].includes(item[getCountryMap(country)])}
                    onClick={() => handleDownload(item, getCountryMap(country))}
                  >
                    {item[getCountryMap(country)] === 1 ? (
                      <DownloadIcon className={styles.downloadIcon} />
                    ) : item[getCountryMap(country)] === 2 ? (
                      <div className={styles.new}>
                        new <DownloadIcon className={styles.downloadIcon} />
                      </div>
                    ) : item[getCountryMap(country)] === 3 ? (
                      <Tooltip title={ITALY_DOWNLOAD_MSG}>
                        <div className={styles.disabled}>
                          <DownloadIcon className={styles.downloadIcon} />
                        </div>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </RippleElement>
                ))}
              </div>
            ))}
          </div>
        </div>
        {loader && <Loader type="content" />}
      </div>
    </div>
  );
};

export default Compliance;
